import React, { useEffect, useState, useCallback } from 'react';
import './HomePage.css';
import {
  IconNavFridge, IconWarning, IconNavFile,
  IconClock, IconNavCalendar, IconNavShopping,
} from '../components/icons';

const FAQ_ITEMS = [
  { q: 'Ist Strixly kostenlos?', a: 'Ja — während der Beta-Phase ist Strixly vollständig kostenlos. Keine Kreditkarte, keine versteckten Kosten. Nach der Beta wird es ein kostenloses Grundmodell geben sowie ein optionales Pro-Abo für erweiterte Funktionen wie unbegrenzte Scans und MHD-Tracking. Registrierte Beta-Nutzer werden rechtzeitig per E-Mail informiert.' },
  { q: 'Was passiert wenn die Handschrift unleserlich ist?', a: 'Unsere KI erkennt die meisten Handschriften zuverlässig — auch bei Umlauten und alten Schriftarten. Wenn trotzdem etwas fehlt: du siehst das Ergebnis sofort und kannst einzelne Felder manuell korrigieren oder ergänzen. Bei sehr schlechter Qualität hilft mehr Licht oder ein gerader Blickwinkel auf das Rezept.' },
  { q: 'Welche Allergene werden erkannt?', a: 'Strixly prüft alle 14 EU-weit vorgeschriebenen Hauptallergene: Gluten, Krebstiere, Eier, Fisch, Erdnüsse, Soja, Milch, Schalenfrüchte, Sellerie, Senf, Sesam, Schwefeldioxid, Lupinen und Weichtiere.' },
  { q: 'Wo werden meine Daten gespeichert?', a: 'Alle Daten liegen auf Servern in Europa (EU, Deutschland). Vollständig DSGVO-konform, keine Datenweitergabe an Dritte.' },
  { q: 'Was ist MHD-Tracking?', a: 'Beim Kühlschrank-Scan schätzt die KI die Haltbarkeit deiner Lebensmittel und zeigt kritische Produkte sofort an — damit nichts mehr weggeschmissen wird.' },
  { q: 'Was passiert mit meinen Daten wenn ich aufhöre?', a: 'Deine Rezepte gehören dir. Du kannst deine Daten jederzeit als JSON-Datei herunterladen — über die Einstellungen unter "Mein Konto". Wenn du dein Konto löschst, werden alle gespeicherten Rezepte, Bilder und persönlichen Daten vollständig und unwiderruflich entfernt. Keine Weitergabe, kein Backup bei Dritten.' },
];

function HomePage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '' });

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, src: '', alt: '' });
    document.body.style.overflow = '';
  }, []);

  // Scroll reveal
  useEffect(() => {
    const els = document.querySelectorAll('.lp-root .up');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) { el.classList.add('in'); }
      else { obs.observe(el); }
    });
    return () => obs.disconnect();
  }, []);

  // ESC schließt Lightbox
  useEffect(() => {
    if (!lightbox.open) return;
    const handler = (e) => { if (e.key === 'Escape') closeLightbox(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightbox.open, closeLightbox]);

  return (
    <div className="lp-root">

      {/* NAV */}
      <nav className="nav">
        <a href="/" className="nav-logo">Strix<em>ly</em></a>
        <ul className="nav-links">
          <li><a href="#how">Wie es funktioniert</a></li>
          <li><a href="#features">Features</a></li>

          <li><a href="#faq">FAQ</a></li>
        </ul>
        <a href="/login" className="nav-login">Anmelden</a>
        <a href="/rezepte" className="nav-cta">Kostenlos starten →</a>
      </nav>

      {/* HERO */}
      <section className="lp-hero" style={{backgroundImage: "url('/assets/hero-landing.jpeg')"}}>
        <div className="lp-hero-inner">
            <div className="lp-hero-badge">
              <span className="lp-hero-bdot"></span>
              Strixly — dein KI-Küchen-Assistent
            </div>
            <h1 className="lp-hero-h1">
              Der Kopf ist voll, der Kühlschrank auch —<br />
              aber die <em>Idee fehlt?</em>
            </h1>
            <p className="lp-hero-sub">
              Du machst ein Foto. Strixly macht den Plan. Erhalte sofort leckere
              Rezeptideen für die Zutaten, die weg müssen. Ohne langes Suchen, ohne Stress.
            </p>
            <a href="/rezepte" className="lp-btn-primary">
              Jetzt kostenlos ausprobieren →
            </a>
            <p className="lp-hero-microcopy">
              Kein Login nötig · Direkt stöbern · In 30 Sekunden registriert
            </p>
        </div>
      </section>


      {/* HOW IT WORKS */}
      <section className="light-section" id="how">
        <div className="section-inner center">
          <div className="up">
            <span className="eyebrow">Wie es funktioniert</span>
            <h2 className="h2">In 3 Schritten zum<br /><em>perfekten Rezept.</em></h2>
          </div>
          <div className="steps-grid">
            <div className="step-card up">
              <div className="step-num">01</div>
              <span className="step-emoji">📸</span>
              <div className="step-title">Foto aufnehmen</div>
              <div className="step-text">Kühlschrank, Vorratskammer oder handgeschriebene Rezeptkarte — einfach fotografieren.</div>
            </div>
            <div className="step-card up" style={{transitionDelay: '0.1s'}}>
              <div className="step-num">02</div>
              <span className="step-emoji">🤖</span>
              <div className="step-title">KI analysiert</div>
              <div className="step-text">Zutaten erkannt, Allergene geprüft, Rezepte gefunden — alles automatisch in unter 30 Sekunden.</div>
            </div>
            <div className="step-card up" style={{transitionDelay: '0.2s'}}>
              <div className="step-num">03</div>
              <span className="step-emoji">🍽️</span>
              <div className="step-title">Kochen & genießen</div>
              <div className="step-text">Rezept öffnen, fehlende Zutaten auf die Einkaufsliste — oder direkt loslegen.</div>
            </div>
          </div>
        </div>
      </section>

      {/* 800€ SCHMERZPUNKT */}
      <section className="lp-pain-section">
        <div className="section-inner">
          <div className="lp-pain-inner up">
            <div className="lp-pain-num-box">
              <div className="lp-pain-num">800€</div>
              <div className="lp-pain-num-sub">könnte jeder Haushalt<br />pro Jahr sparen</div>
              <div className="lp-pain-num-src">Quelle: WWF Österreich / BOKU Wien</div>
            </div>
            <div className="lp-pain-text">
              <span className="eyebrow">Dein persönlicher Bonus für die Haushaltskasse</span>
              <h2 className="h2">In jedem Kühlschrank steckt<br /><em>ein kleiner Schatz.</em></h2>
              <p>Das Leben ist vollgepackt. Nach einem langen Tag fehlt einfach die Energie
              um zu planen. Das ist menschlich — und genau da hilft Strixly.</p>
              <p>Oft fehlt im Alltag nur der Blick dafür, was aus dem Rest Sellerie oder dem
              Joghurt noch werden kann. <strong>Strixly schenkt dir diesen Blick — und schont
              dabei deine Geldbörse.</strong></p>
              <a href="/rezepte" className="lp-btn-primary" style={{marginTop: '8px', display: 'inline-block'}}>
                Geld sparen &amp; Lebensmittel retten →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SPLIT: MHD Tracking */}
      <section className="cream-section">
        <div className="section-inner">
          <div className="split up">
            <div className="split-text">
              <span className="split-eyebrow">MHD-Schätzung</span>
              <h2 className="split-h">Nichts mehr<br /><em>wegschmeißen.</em></h2>
              <p className="split-p">Beim Kühlschrank-Scan schätzt die KI automatisch die Haltbarkeit deiner Lebensmittel. Kritische Produkte werden sofort hervorgehoben — direkt neben den passenden Rezeptvorschlägen aus deinem Kühlschrank.</p>
              <div className="checks">
                <div className="check"><div className="ck">✓</div>Automatische Schätzung beim Scan</div>
                <div className="check"><div className="ck">✓</div>Ampel-System: Rot / Amber / Grün</div>
                <div className="check"><div className="ck">✓</div>Rezeptvorschläge für kritische Zutaten</div>
                <div className="check"><div className="ck">✓</div>Direkt nach dem Scan sichtbar</div>
              </div>
            </div>
            <div className="lp-prio-wrap">
              <div className="lp-sec-label">⏱ Bitte bald verwerten</div>
              <div className="lp-prio-list">
                {[
                  { name: 'Hackfleisch', pill: 'HEUTE!', pillClass: 'lp-pill-today', fill: 98, fillClass: 'lp-pf-red' },
                  { name: 'Joghurt', pill: 'NOCH 3 T.', pillClass: 'lp-pill-amber', fill: 60, fillClass: 'lp-pf-amber' },
                  { name: 'Paprika', pill: 'NOCH 5 T.', pillClass: 'lp-pill-amber', fill: 40, fillClass: 'lp-pf-amber' },
                ].map((item) => (
                  <div className="lp-prio-card" key={item.name}>
                    <div className="lp-prio-normal">
                      <div className="lp-prio-top">
                        <span className="lp-prio-name">{item.name}</span>
                        <span className={`lp-pill ${item.pillClass}`}>{item.pill}</span>
                      </div>
                      <div className="lp-prio-sub">Schätzwert ab letztem Scan</div>
                      <div className="lp-prio-bar"><div className={`lp-prio-fill ${item.fillClass}`} style={{width: `${item.fill}%`}}></div></div>
                    </div>
                    <div className="lp-prio-actions">
                      <button className="lp-pa-btn lp-pa-done"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></button>
                      <button className="lp-pa-btn lp-pa-cal"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg></button>
                      <button className="lp-pa-btn lp-pa-del"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="lp-prio-hint">Beispieldaten · Scanne deinen Kühlschrank für echte Haltbarkeits-Schätzungen</p>
            </div>
          </div>
        </div>
      </section>

      {/* SPLIT: Oma Rezepte */}
      <section className="light-section">
        <div className="section-inner">
          <div className="split reverse up">
            <div className="split-text">
              <span className="split-eyebrow" style={{color: 'var(--lp-amber)'}}>Handschriften-OCR</span>
              <h2 className="split-h">Omas Liebe<br /><em>digital bewahren.</em></h2>
              <p className="split-p">Alte Familienrezepte vor dem Vergilben retten — bevor sie für immer verloren gehen. Strixly liest was Google nicht versteht und bewahrt deine Küchentradition für die nächste Generation.</p>
              <div className="checks">
                <div className="check"><div className="ck">✓</div>Handschrift und Druckschrift werden erkannt</div>
                <div className="check"><div className="ck">✓</div>Zutaten, Mengen und Schritte strukturiert</div>
                <div className="check"><div className="ck">✓</div>Portionen-Rechner automatisch</div>
                <div className="check"><div className="ck">✓</div>Per WhatsApp mit Familie teilen</div>
              </div>
            </div>
            <div>
              <div className="oma-mockup">
                <div className="oma-top">📄 Original — handgeschrieben</div>
                <div className="oma-hw">
                  <div className="handwrite">
                    Omas Apfelstrudel<br />
                    500g Mehl, 1 Ei, Prise Salz<br />
                    250ml lauwarmes Wasser<br />
                    6 Äpfel, Zimt, Zucker<br />
                    Butter zum Bestreichen
                  </div>
                </div>
                <div className="oma-divider">✓ Strixly hat digitalisiert</div>
                <div className="oma-result">
                  <div className="oma-rtitle">📖 Omas Apfelstrudel</div>
                  <div className="oma-step"><div className="ostep-n">1</div><span>Mehl, Ei, Salz und Wasser zu einem glatten Teig verkneten</span></div>
                  <div className="oma-step"><div className="ostep-n">2</div><span>Äpfel schälen, entkernen und in dünne Scheiben schneiden</span></div>
                  <div className="oma-step"><div className="ostep-n">3</div><span>Teig dünn ausrollen, mit Butter bestreichen und backen</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GRÜNDER-STORY */}
      <section className="light-section">
        <div className="section-inner">
          <div className="lp-founder-inner up">
            <div className="lp-founder-photo">
              <div className="lp-founder-placeholder">
                <img src="/screens/foto-markus.jpg" alt="Markus" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} />
              </div>
            </div>
            <div>
              <span className="eyebrow">Warum ich Strixly gebaut habe</span>
              <div className="lp-founder-qdeco">❝</div>
              <blockquote className="lp-founder-quote">
                Ich bin seit 15 Jahren Koch. Und es hat mich jeden Tag zerrissen, wie viel
                gutes Essen im Müll landet. Nicht weil die Menschen verschwenderisch sind —
                sondern weil der Alltag nach der Arbeit oft keine Ideen mehr zulässt.
                <br /><br />
                Ich wollte genau diesen Moment lösen: Den ratlosen Blick in den Kühlschrank.
                <br /><br />
                Von einem Koch — für deine Küche.
              </blockquote>
              <div className="lp-founder-byline">
                <span className="lp-founder-name">Markus</span>
                <span className="lp-founder-role">Koch &amp; Gründer von Strixly</span>
              </div>
              <div className="lp-founder-beta">
                <div className="lp-founder-beta-text">
                  Strixly ist aktuell in der Beta. Keine Millionen-Investoren — nur eine echte
                  Mission. Melde dich kostenlos an und hilf uns mit deinem Feedback die App
                  für alle besser zu machen.
                </div>
                <a href="/rezepte" className="lp-btn-primary" style={{marginTop: '16px', display: 'inline-block'}}>
                  App kostenlos ausprobieren →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INKLUSION */}
      <section className="cream-section">
        <div className="section-inner center">
          <div className="up">
            <span className="eyebrow">Damit jeder mit am Tisch sitzt</span>
            <h2 className="h2">Weil Essen<br /><em>Gemeinschaft bedeutet.</em></h2>
            <p className="lead" style={{maxWidth: '560px', margin: '12px auto 0'}}>
              Strixly passt diskret im Hintergrund an — damit beim Essen niemand das
              Gefühl hat, anders zu sein.
            </p>
          </div>
          <div className="lp-incl-grid up">
            <div className="lp-incl-card">
              <div className="lp-incl-icon">🥛</div>
              <div className="lp-incl-title">Laktose oder Gluten?</div>
              <div className="lp-incl-text">Strixly prüft automatisch alle 14 EU-Hauptallergene bei jedem Rezept — sichtbar, bevor du anfängst zu kochen.</div>
            </div>
            <div className="lp-incl-card">
              <div className="lp-incl-icon">🥕</div>
              <div className="lp-incl-title">Vegetarisch oder vegan?</div>
              <div className="lp-incl-text">Mit einem Klick filterst du die Rezeptliste nach deinen Vorlieben. Kein Suchen, kein Umrechnen.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="cream-section" id="features">
        <div className="section-inner center">
          <div className="up">
            <span className="eyebrow">Was Strixly kann</span>
            <h2 className="h2">Mehr als ein<br /><em>Rezept-Scanner.</em></h2>
          </div>
          <div className="feat-grid">
            {[
              { icon: <IconNavFridge size={24} />, plan: 'fp-free', label: 'Kostenlos', title: 'Kühlschrank-Scan', text: 'KI erkennt alle Zutaten automatisch und schlägt passende Rezepte vor — in Echtzeit.', delay: 0 },
              { icon: <IconWarning size={24} />, plan: 'fp-free', label: 'Kostenlos', title: 'Allergen-Profile', text: '14 EU-Allergene (Laktose, Gluten, Nüsse u.v.m.) werden bei jedem Rezept automatisch geprüft.', delay: 0.08 },
              { icon: <IconNavFile size={24} />, plan: 'fp-free', label: 'Kostenlos', title: 'Handschriften-OCR', text: 'Alte Rezeptkarten werden erkannt und digital strukturiert — Zutaten, Mengen, Schritte.', delay: 0.16 },
              { icon: <IconClock size={24} />, plan: 'fp-pro', label: 'Pro', title: 'MHD-Tracking', text: 'Haltbarkeit im Blick behalten, Warnungen erhalten und Lebensmittelverschwendung reduzieren.', delay: 0.08 },
              { icon: <IconNavCalendar size={24} />, plan: 'fp-soon', label: 'Bald', title: 'Wochenplaner', text: 'Mahlzeiten für die Woche planen, Einkaufsliste automatisch generieren und teilen.', delay: 0.16 },
              { icon: <IconNavShopping size={24} />, plan: 'fp-free', label: 'Kostenlos', title: 'Einkaufsliste', text: 'Zutaten per Klick auf die Liste, per WhatsApp teilen — fertig für den nächsten Einkauf.', delay: 0.24 },
            ].map((f) => (
              <div className="feat-card up" key={f.title} style={{transitionDelay: `${f.delay}s`}}>
                <div className="feat-icon">{f.icon}</div>
                <span className={`feat-plan ${f.plan}`}>{f.label}</span>
                <div className="feat-title">{f.title}</div>
                <div className="feat-text">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* VERTRAUEN */}
      <section className="light-section">
        <div className="section-inner center">
          <div className="up" style={{marginBottom: '8px'}}>
            <span className="eyebrow">Warum Strixly</span>
            <h2 className="h2">Gebaut mit Herz.<br /><em>Geschützt mit Verstand.</em></h2>
          </div>
          <div className="lp-trust-grid up">
            <div className="lp-trust-card">
              <span className="lp-trust-flag">🧑‍🍳</span>
              <div className="lp-trust-title">Um anderen zu helfen, ihren Alltag besser in den Griff zu bekommen.</div>
              <div className="lp-trust-text">Dafür wurde Strixly gebaut. Hier schreibt kein Konzern, sondern ein Koch — mit dem Herz am rechten Fleck.</div>
            </div>
            <div className="lp-trust-card">
              <span className="lp-trust-flag">🔒</span>
              <div className="lp-trust-title">Was du eingibst, gehört dir.</div>
              <div className="lp-trust-text">Gespeichert in der Heimat. Garantiert nach Europäischem Datenschutz (DSGVO). Wir retten Lebensmittel — nicht Daten.</div>
            </div>
            <div className="lp-trust-card">
              <span className="lp-trust-flag">🦉</span>
              <div className="lp-trust-title">Strixly die Eule: Diskret. Im Hintergrund. Immer da wenn du sie brauchst.</div>
              <div className="lp-trust-text">Damit du dich wieder um das Wesentliche im Alltag kümmern kannst.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cream-section" id="faq">
        <div className="section-inner narrow">
          <div className="up" style={{textAlign: 'center', marginBottom: '40px'}}>
            <span className="eyebrow">FAQ</span>
            <h2 className="h2">Häufige<br /><em>Fragen.</em></h2>
          </div>
          <div className="faq-list">
            {FAQ_ITEMS.map((item, idx) => {
              const isOpen = openFaq === idx;
              const qId = `faq-q-${idx}`;
              const aId = `faq-a-${idx}`;
              return (
                <div className={`faq-item${isOpen ? ' open' : ''}`} key={idx}>
                  <button
                    className="faq-q"
                    id={qId}
                    aria-expanded={isOpen}
                    aria-controls={aId}
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                  >
                    <span>{item.q}</span>
                    <span className="faq-tog" aria-hidden="true">+</span>
                  </button>
                  <div
                    className="faq-a"
                    id={aId}
                    role="region"
                    aria-labelledby={qId}
                  >
                    {item.a}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <h2 className="cta-h2">Für jeden Abend.<br /><em>Für jeden am Tisch.</em></h2>
        <p className="cta-sub">Einfach loslegen. Kostenlos. Ohne Verpflichtung.</p>
        <a href="/rezepte" className="cta-main-btn">Jetzt kostenlos ausprobieren →</a>
        <div className="cta-trust">
          <div className="ct-item">Keine Kreditkarte</div>
          <div className="ct-item">DSGVO-konform</div>
          <div className="ct-item">Server in Europa (EU)</div>
          <div className="ct-item">Jederzeit kündbar</div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="footer-logo">Strix<em>ly</em></div>
        <div className="footer-links">
          <a href="/datenschutz">Datenschutz</a>
          <a href="/impressum">Impressum</a>
          <a href="/agb">AGB</a>
          <a href="mailto:info@strixly.at">Kontakt</a>
        </div>
        <div className="footer-copy">© 2026 Strixly · strixly.at</div>
        <div className="lp-footer-disclaimer">
          Strixly nutzt Künstliche Intelligenz. Bitte prüfe generierte Rezepte,
          Haltbarkeiten und Allergene stets selbst.
        </div>
      </footer>

      {/* LIGHTBOX */}
      {lightbox.open && (
        <div className="lp-lightbox" onClick={closeLightbox}>
          <button className="lp-lightbox-close" onClick={closeLightbox}>✕</button>
          <img src={lightbox.src} alt={lightbox.alt} onClick={e => e.stopPropagation()} />
        </div>
      )}

    </div>
  );
}

export default HomePage;
